import React, { useState, useEffect, memo, useMemo } from 'react';
import { supabase } from '../supabase/config';
import { logger } from '../utils/logger';

const ActivityTimeline = memo(({ childId, dateRange = 'all' }) => {
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState({
    subject: 'all',
    activityType: 'all'
  });

  useEffect(() => {
    if (childId) {
      loadActivities();
    }
  }, [childId, dateRange]);

  const loadActivities = async () => {
    if (!childId) return;

    setIsLoading(true);
    try {
      let query = supabase
        .from('child_activity_log')
        .select('*')
        .eq('child_id', childId)
        .order('created_at', { ascending: false })
        .limit(100);

      // Apply date range filter
      if (dateRange !== 'all') {
        const now = new Date();
        const startDate = new Date();
        
        if (dateRange === 'week') {
          startDate.setDate(now.getDate() - 7);
        } else if (dateRange === 'month') {
          startDate.setMonth(now.getMonth() - 1);
        }
        
        query = query.gte('created_at', startDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Error loading activities:', error);
        // Fallback: return empty array if table doesn't exist yet
        setActivities([]);
      } else {
        setActivities(data || []);
      }
    } catch (error) {
      logger.error('Error loading activities:', error);
      setActivities([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter activities
  const filteredActivities = useMemo(() => {
    return activities.filter(activity => {
      if (filter.subject !== 'all' && activity.subject !== filter.subject) {
        return false;
      }
      if (filter.activityType !== 'all' && activity.activity_type !== filter.activityType) {
        return false;
      }
      return true;
    });
  }, [activities, filter]);

  // Get unique subjects and activity types for filters
  const uniqueSubjects = useMemo(() => {
    const subjects = new Set(activities.map(a => a.subject).filter(Boolean));
    return Array.from(subjects).sort();
  }, [activities]);

  const uniqueActivityTypes = useMemo(() => {
    const types = new Set(activities.map(a => a.activity_type));
    return Array.from(types).sort();
  }, [activities]);

  // Format activity type for display
  const formatActivityType = (type) => {
    const types = {
      'lesson_completed': '📚 Lesson Completed',
      'achievement_unlocked': '🏆 Achievement Unlocked',
      'streak_milestone': '🔥 Streak Milestone',
      'subject_started': '🎯 Subject Started',
      'progress_update': '📈 Progress Update',
      'goal_reached': '🎯 Goal Reached'
    };
    return types[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format subject name
  const formatSubject = (subject) => {
    const names = {
      phonics: 'Phonics',
      math: 'Math',
      reading: 'Reading',
      spelling: 'Spelling',
      science: 'Science',
      social: 'Social Studies',
      art: 'Art',
      geography: 'Geography',
      history: 'History',
      skipcounting: 'Skip Counting'
    };
    return names[subject] || subject?.charAt(0).toUpperCase() + subject?.slice(1) || 'General';
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Group activities by date
  const groupedActivities = useMemo(() => {
    const groups = {};
    filteredActivities.forEach(activity => {
      const date = new Date(activity.created_at);
      const dateKey = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(activity);
    });
    return groups;
  }, [filteredActivities]);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading activity timeline...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <select
              value={filter.subject}
              onChange={(e) => setFilter({ ...filter, subject: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Subjects</option>
              {uniqueSubjects.map(subject => (
                <option key={subject} value={subject}>
                  {formatSubject(subject)}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Activity Type
            </label>
            <select
              value={filter.activityType}
              onChange={(e) => setFilter({ ...filter, activityType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              {uniqueActivityTypes.map(type => (
                <option key={type} value={type}>
                  {formatActivityType(type)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilter({ subject: 'all', activityType: 'all' })}
              className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Timeline */}
      {filteredActivities.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-4xl mb-4">📭</div>
          <p className="text-gray-600">No activities found</p>
          <p className="text-sm text-gray-500 mt-2">
            Activities will appear here as your child learns
          </p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

          <div className="space-y-6">
            {Object.entries(groupedActivities).map(([dateKey, dayActivities]) => (
              <div key={dateKey} className="relative">
                {/* Date header */}
                <div className="flex items-center mb-4">
                  <div className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold">
                    {dateKey}
                  </div>
                  <div className="ml-4 text-sm text-gray-500">
                    {dayActivities.length} {dayActivities.length === 1 ? 'activity' : 'activities'}
                  </div>
                </div>

                {/* Activities for this date */}
                <div className="space-y-4 ml-8">
                  {dayActivities.map((activity, index) => (
                    <div
                      key={activity.id || index}
                      className="relative bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                    >
                      {/* Timeline dot */}
                      <div className="absolute -left-12 top-6 w-3 h-3 bg-purple-500 rounded-full border-2 border-white"></div>

                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">
                              {activity.activity_type === 'lesson_completed' && '📚'}
                              {activity.activity_type === 'achievement_unlocked' && '🏆'}
                              {activity.activity_type === 'streak_milestone' && '🔥'}
                              {activity.activity_type === 'subject_started' && '🎯'}
                              {activity.activity_type === 'progress_update' && '📈'}
                              {activity.activity_type === 'goal_reached' && '🎯'}
                              {!['lesson_completed', 'achievement_unlocked', 'streak_milestone', 'subject_started', 'progress_update', 'goal_reached'].includes(activity.activity_type) && '📝'}
                            </span>
                            <span className="font-semibold text-gray-900">
                              {formatActivityType(activity.activity_type)}
                            </span>
                            {activity.subject && (
                              <span className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded">
                                {formatSubject(activity.subject)}
                              </span>
                            )}
                          </div>
                          
                          {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                            <div className="text-sm text-gray-600 mt-2">
                              {activity.metadata.score && (
                                <span className="mr-4">Score: {activity.metadata.score}</span>
                              )}
                              {activity.metadata.accuracy && (
                                <span className="mr-4">Accuracy: {activity.metadata.accuracy}%</span>
                              )}
                              {activity.metadata.achievement && (
                                <span>Achievement: {activity.metadata.achievement}</span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="text-sm text-gray-500 ml-4">
                          {formatDate(activity.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

ActivityTimeline.displayName = 'ActivityTimeline';

export default ActivityTimeline;

